import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { BellIcon } from '@/components/icons';
import { Avatar } from '@/components/ui';
import { useFotoUrlPorId } from '@/lib/admin-store';
import {
  useNotifications,
  useUnreadCount,
  useNotificationActions,
  type Notification,
} from '@/lib/notifications-store';
import {
  EVENT_TYPES,
  type NotificationEventType,
} from '@/lib/notification-preferences';
import { formatRelativeDate, formatDataHoraSegundos } from '@/lib/format-date';
// AUDITORIA-V2-001 (BUG-V2-050): mapping workflow.slug → dept_slug pro
// fallback de payloads antigos sem `deptSlug`.
import { workflowToDept } from '@/lib/workflow-mapping';
// UI-2 / TD-V2-PERM-06: botões inline aprovar/rejeitar em notification.
import { TaskApprovalActions } from './TaskApprovalActions';
// UI-3 / TD-V2-PERM-07: botões inline aceitar/recusar convite de tarefa.
import { TaskInvitationActions } from './TaskInvitationActions';

interface NotificationsBellProps {
  userId: string;
}

const EVENT_LABELS: Record<NotificationEventType, string> = EVENT_TYPES.reduce(
  (acc, evt) => {
    acc[evt.slug] = evt.label;
    return acc;
  },
  {} as Record<NotificationEventType, string>
);

/** Ícone por tipo — render no dropdown. Decisão 3 do commit 6 (🤖 pra automação). */
const EVENT_ICONS: Partial<Record<NotificationEventType, string>> = {
  automation_triggered: '🤖',
  mention_in_comment: '💬',
  deal_assigned: '📋',
  task_deadline_approaching: '⏰',
  comment_in_followed_deal: '💬',
};

/**
 * Sino de notificações na TopBar.
 *
 * - Badge vermelho com contagem se houver não-lidas
 * - Click abre dropdown
 * - Filtro "todas / não-lidas" (default: não-lidas)
 * - Click em notificação marca lida (em R7+ também navega pro contexto)
 * - Botão "Marcar todas como lidas"
 * - Link "Preferências" pra /perfil/notificacoes
 */
export function NotificationsBell({ userId }: NotificationsBellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifications = useNotifications(userId);
  const unreadCount = useUnreadCount(userId);
  const { markRead, markAllRead } = useNotificationActions(userId);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'unread' | 'all'>('unread');
  const containerRef = useRef<HTMLDivElement>(null);

  // UI-2 / TD-V2-PERM-06: refresh notifications após decisão de aprovação
  // (back-end cria notification 'task_approval_decided' pro solicitante e
  // marca a 'task_approval_request' do admin como lida).
  function refreshNotifications() {
    void queryClient.invalidateQueries({ queryKey: ['v1', 'notifications', userId] });
  }

  // Click fora fecha
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const visiveis = useMemo(() => {
    const arr =
      filter === 'unread' ? notifications.filter((n) => !n.lida) : [...notifications];
    // Mais recentes em cima
    arr.sort(
      (a, b) => new Date(b.criadoEmIso).getTime() - new Date(a.criadoEmIso).getTime(),
    );
    return arr;
  }, [filter, notifications]);

  // M-5 (FEAT-V2-028): agrupa por janela de tempo pra card-rico UI.
  // "Hoje" = hoje (00:00 local), "Esta semana" = últimos 7 dias, resto = "Anteriores".
  const grupos = useMemo(() => groupByTimeBucket(visiveis), [visiveis]);

  function handleClickNotification(n: Notification) {
    if (!n.lida) void markRead(n.id);
    setOpen(false);

    // BUG-V2-043: navegação universal — handler único pros 12 types
    // canônicos. Antes: hardcoded só 'automation_triggered' e
    // 'mention_in_comment'. Memória feedback_design_system_notifications
    // estabeleceu a regra "click no card inteiro: navigate(link_url) +
    // markAsRead(id)" em 21/05; implementação só chegou agora.

    // 1. linkUrl canônico (payload do backend) — preferido. Funciona pra
    //    qualquer type que tenha template com linkUrl preenchido. Cobre
    //    notifs criadas pós-BUG-V2-043.
    const linkUrl = typeof n.payload.linkUrl === 'string' ? n.payload.linkUrl : null;
    if (linkUrl && linkUrl.length > 0) {
      navigate(linkUrl);
      return;
    }

    // 2. Fallback compat retroativa: notifs antigas no banco (pré-deploy)
    //    sem payload.linkUrl. Constrói rota a partir de cardId +
    //    deptSlug|workflow|workflowSlug.
    //
    //    AUDITORIA-V2-001 (BUG-V2-050): formato canônico é
    //    `/menu/{deptSlug}/{workflowSlug}?openCard={cardId}`. Precedência:
    //      1. payload.deptSlug (canonico — backend novo)
    //      2. workflowToDept(payload.workflowSlug) (fallback hardcoded
    //         pra payloads antigos sem deptSlug)
    //      3. 'comercial' fallback grace
    const cardId = typeof n.payload.cardId === 'string' ? n.payload.cardId : null;
    if (cardId) {
      const workflow =
        typeof n.payload.workflowSlug === 'string'
          ? n.payload.workflowSlug
          : typeof n.payload.workflow === 'string'
            ? n.payload.workflow
            : 'comercial';
      const dept =
        typeof n.payload.deptSlug === 'string' && n.payload.deptSlug.length > 0
          ? n.payload.deptSlug
          : workflowToDept(workflow);
      navigate(
        `/menu/${encodeURIComponent(dept)}/${encodeURIComponent(workflow)}?openCard=${encodeURIComponent(cardId)}`,
      );
      return;
    }

    // 3. Sem destino — só registra warn (Sentry capta em prod via console
    //    breadcrumb). NÃO bloqueia user.
    console.warn(
      `[notifications] notif ${n.id} type=${n.type} sem destino de navegação (sem payload.linkUrl/cardId)`,
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={
          unreadCount > 0
            ? `Notificações (${unreadCount} não lidas)`
            : 'Notificações'
        }
        data-testid="bell-button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-3 md:p-2 rounded-lg text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span
            data-testid="bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red text-white text-xs font-medium flex items-center justify-center tabular-nums"
            aria-hidden
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Lista de notificações"
          data-testid="bell-dropdown"
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-xl z-30"
        >
          {/* Header do dropdown */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-line dark:border-line-dark">
            <div className="flex items-center gap-1">
              <FilterChip active={filter === 'unread'} onClick={() => setFilter('unread')}>
                Não-lidas {unreadCount > 0 && `(${unreadCount})`}
              </FilterChip>
              <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                Todas
              </FilterChip>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="bell-mark-all-read"
                onClick={markAllRead}
                className="text-xs text-red hover:text-red-deep transition-colors"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Lista — M-5 (FEAT-V2-028): card-rico com agrupamento temporal +
              avatar do actor + título/body do template + ação inline. */}
          <div className="max-h-[28rem] overflow-y-auto">
            {visiveis.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-3 dark:text-ink-dark-3 italic">
                {filter === 'unread'
                  ? 'Nada de novo por aqui'
                  : 'Você ainda não tem notificações'}
              </p>
            ) : (
              <div data-testid="bell-grouped">
                {grupos.map(([bucketLabel, items]) =>
                  items.length === 0 ? null : (
                    <section key={bucketLabel} data-testid={`bell-bucket-${bucketLabel}`}>
                      <h3 className="px-4 pt-3 pb-1 text-xs uppercase tracking-wide font-medium text-ink-3 dark:text-ink-dark-3">
                        {bucketLabel}
                      </h3>
                      <ul>
                        {items.map((n) => (
                          <NotificationCard
                            key={n.id}
                            notification={n}
                            onClick={() => handleClickNotification(n)}
                            onActionDecided={refreshNotifications}
                          />
                        ))}
                      </ul>
                    </section>
                  ),
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-line dark:border-line-dark text-center">
            <Link
              to="/perfil/notificacoes"
              onClick={() => setOpen(false)}
              className="text-xs text-ink-2 dark:text-ink-dark-2 hover:text-red transition-colors"
            >
              Configurar preferências →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-pill transition-colors ${
        active
          ? 'bg-red text-white'
          : 'text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
      }`}
    >
      {children}
    </button>
  );
}

// ─── M-5 (FEAT-V2-028): card-rico ────────────────────────────────────

type TimeBucket = 'Hoje' | 'Esta semana' | 'Anteriores';

/**
 * Agrupa notificações por janela temporal local. Tuples preservam ordem
 * de render — começa em Hoje, termina em Anteriores. Ordem dentro do
 * bucket segue a do array (já desc por criadoEmIso no caller).
 */
function groupByTimeBucket(items: Notification[]): Array<[TimeBucket, Notification[]]> {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfWeek = startOfDay - 6 * 24 * 60 * 60 * 1000;
  const hoje: Notification[] = [];
  const semana: Notification[] = [];
  const anteriores: Notification[] = [];
  for (const n of items) {
    const t = new Date(n.criadoEmIso).getTime();
    if (t >= startOfDay) hoje.push(n);
    else if (t >= startOfWeek) semana.push(n);
    else anteriores.push(n);
  }
  return [
    ['Hoje', hoje],
    ['Esta semana', semana],
    ['Anteriores', anteriores],
  ];
}

interface NotificationCardProps {
  notification: Notification;
  onClick: () => void;
  onActionDecided: () => void;
}

/**
 * Card-rico de notificação — substitui o item de lista simples (M-5).
 *
 * Layout:
 *   ┌─ avatar (do actor, se houver) | conteúdo + meta + ações ─┐
 *
 * Conteúdo prioriza:
 *   1. payload.title (backend renderizou template — ex: "Carlos comentou")
 *   2. EVENT_LABELS[type] (fallback)
 *
 * Body prioriza:
 *   1. payload.body (template renderizado)
 *   2. payload.message / payload.summary
 *
 * Ícone do tipo (🤖, 💬, 📋, ⏰) aparece pequeno acima do title.
 * Meta mostra "via {automationNome}" pra automation_triggered.
 * Botões inline pra task_approval_request / task_invitation_received.
 */
function NotificationCard({ notification: n, onClick, onActionDecided }: NotificationCardProps) {
  const actorNome =
    typeof n.payload.actorNome === 'string' && n.payload.actorNome.trim().length > 0
      ? n.payload.actorNome
      : null;
  // I6b: foto real do actor — o payload guarda actorUserId; resolvemos o
  // fotoUrl pelo lookup leve (foto→iniciais é fallback do próprio Avatar).
  const fotoDe = useFotoUrlPorId();
  const actorUserId =
    typeof n.payload.actorUserId === 'string' ? n.payload.actorUserId : null;
  const title =
    typeof n.payload.title === 'string' && n.payload.title.trim().length > 0
      ? n.payload.title
      : EVENT_LABELS[n.type] ?? n.type;
  const body =
    typeof n.payload.body === 'string' && n.payload.body.trim().length > 0
      ? n.payload.body
      : typeof n.payload.message === 'string'
        ? n.payload.message
        : typeof n.payload.summary === 'string'
          ? n.payload.summary
          : null;
  const eventIcon = EVENT_ICONS[n.type];

  return (
    <li
      data-testid={`bell-notif-${n.id}`}
      className={`border-b border-line dark:border-line-dark last:border-b-0 ${
        !n.lida ? 'bg-red-soft/30 dark:bg-red-soft-dark/30' : ''
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-4 py-3 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* Avatar do actor — usa iniciais como fallback. Ícone de evento
              quando notification é sistema (sem actor). */}
          {actorNome ? (
            <Avatar
              name={actorNome}
              src={fotoDe(actorUserId)}
              size="sm"
              className="flex-shrink-0"
            />
          ) : (
            <span
              aria-hidden
              data-testid={`bell-system-icon-${n.id}`}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-paper-warm dark:bg-paper-dark-warm flex items-center justify-center text-base"
            >
              {eventIcon ?? '🔔'}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 flex items-center gap-1.5 min-w-0">
                {actorNome && eventIcon && (
                  <span aria-hidden className="text-xs leading-none flex-shrink-0">
                    {eventIcon}
                  </span>
                )}
                <span className="truncate">{title}</span>
              </p>
              <span
                className="text-xs text-ink-3 dark:text-ink-dark-3 flex-shrink-0"
                title={formatDataHoraSegundos(n.criadoEmIso)}
              >
                {formatRelativeDate(n.criadoEmIso)}
              </span>
            </div>
            {body && (
              <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-1 line-clamp-2">
                {body}
              </p>
            )}
            {n.type === 'automation_triggered' &&
              typeof n.payload.automationNome === 'string' && (
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5 italic">
                  via {n.payload.automationNome as string}
                </p>
              )}
          </div>
        </div>
      </button>
      {/* UI-2 / TD-V2-PERM-06: ações inline pra task_approval_request. */}
      {n.type === 'task_approval_request' &&
        !n.lida &&
        typeof n.payload.approvalId === 'string' && (
          <div className="px-4 pb-3 pl-[4.5rem]">
            <TaskApprovalActions
              approvalId={n.payload.approvalId}
              onDecided={onActionDecided}
            />
          </div>
        )}
      {/* UI-3 / TD-V2-PERM-07: ações inline pra task_invitation_received. */}
      {n.type === 'task_invitation_received' &&
        !n.lida &&
        typeof n.payload.invitationId === 'string' && (
          <div className="px-4 pb-3 pl-[4.5rem]">
            <TaskInvitationActions
              invitationId={n.payload.invitationId}
              onDecided={onActionDecided}
            />
          </div>
        )}
    </li>
  );
}
